import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  requestProjectsById,
  setResetStateProjectsById,
} from "../../../Redux/ProjectState/ProjectActionCreator";
import { getValuesFromLocalstorage } from "../../../components/Helpers";
import {
  requestAddTimesheet,
  requestEditTimesheets,
  requestGetTimesheetById,
  requestTimesheetsByIdForUserPage,
  setResetAddTimesheet,
  setResetEditTimesheets,
  setResetGetTimesheetById,
  setResetStateTimesheetById,
  setResetStateTimesheetsByIdForUserPage,
  openTimesheetModal,
  closeTimesheetModal,
} from "../../../Redux/TimeSheetState/TimeSheetActionCreator";
import TimeSheet from "../../Employee/TimeSheet";
import CustomDataTable from "../../../components/CustomDataTable";
import Loader from "../../../components/Loader";
import {
  TrendingUpRounded,
  PendingRounded,
  CancelRounded,
  CheckCircleRounded,
} from "@mui/icons-material";
import CustomToolTip from "../../../components/ToolTip";
import moment from "moment";
import Hoc from "../../../components/HOC";
import { motion, AnimatePresence } from "framer-motion";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  requestEmpDashboardList,
  requestEmpDashboardMonthlyHoursList,
  requestTimesheetCount,
  requestWeeklyEfficiency,
  requestEmpWishes,
  setResetStateEmpDashboardList,
  setResetStateEmpDashboardMonthlyHoursList,
  setResetStateTimesheetCount,
} from "../../../Redux/DashboardState/dashBoardActionCreator";
import {
  ClockIcon,
  DocumentTextIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
  ChartBarIcon,
  UserGroupIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as ClockIconSolid,
} from "@heroicons/react/outline";
import Logo from "../../../components/Logo";
import AlertsCard from "../../../components/AlertsCard";
import BirthdayCard from "../../../components/BirthdayCard";
import WorkAnniversaryCard from "../../../components/WorkAnniversaryCard";

// Custom scrollbar styles
const customScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const userDetails = getValuesFromLocalstorage("userDetails");
  const [selectedOption, setSelectedOption] = useState(null);
  const [showBirthdayCard, setShowBirthdayCard] = useState(true);
  const [showWorkAnniversaryCard, setShowWorkAnniversaryCard] = useState(true);
  const [modalTitle, setModalTitle] = useState("");
  const [showCardsOverlay, setShowCardsOverlay] = useState(true);
  const projectsList = useSelector((state) => state.ProjectsByIdReducer);
  // const TimeSheets = useSelector((state) => state.TimeSheetByProjectIdReducerForUserPage);
  const AddTimeSheetSate = useSelector((state) => state.AddTimesheetReducer);
  const timesheetByIdReducer = useSelector(
    (state) => state.GetTimesheetByIdReducer
  );
  const editTimesheetReducer = useSelector(
    (state) => state.EditTimesheetsReducer
  );
  const empDashboard = useSelector((state) => state.EmpDashboardListReducer);
  const empDashboardMonthlyHours = useSelector(
    (state) => state.EmpDashboardMonthlyHoursReducer
  );
  const timesheetCount = useSelector((state) => state.TimesheetCountReducer);

  const weeklyEfficency = useSelector((state) => state.WeeklyEfficiencyReducer);
  const empWishes = useSelector((state) => state.EmpWishesReducer);

  const dispatch = useDispatch();
  const [chartData, setChartData] = useState([]);

  // Modern color palette - wrapped in useMemo to prevent recreation on each render
  const CHART_COLORS_THEME = useMemo(
    () => [
      "#8B5CF6", // Vibrant Pastel Purple
      "#EC4899", // Vibrant Pastel Pink
      "#3B82F6", // Vibrant Pastel Blue
      "#10B981", // Vibrant Pastel Green
      "#F59E0B", // Vibrant Pastel Yellow
      "#EF4444", // Vibrant Pastel Red
    ],
    []
  );

  // Define color scheme with softer pastel colors for status indicators
  const statusColors = useMemo(
    () => ({
      approved: {
        light: "rgba(16, 185, 129, 0.8)", // Specific green for approved
        main: "rgba(16, 185, 129, 0.9)", // Slightly darker for hover
        dark: "rgba(16, 185, 129, 1)", // Full opacity for text
      },
      rejected: {
        light: "rgba(239, 68, 68, 0.8)", // Vibrant red
        main: "rgba(239, 68, 68, 0.9)",
        dark: "rgba(239, 68, 68, 1)",
      },
      pending: {
        light: "rgba(245, 158, 11, 0.8)", // Vibrant amber
        main: "rgba(245, 158, 11, 0.9)",
        dark: "rgba(245, 158, 11, 1)",
      },
      trend: {
        light: "rgba(139, 92, 246, 0.8)", // Vibrant purple
        main: "rgba(139, 92, 246, 0.9)",
        dark: "rgba(139, 92, 246, 1)",
      }
    }),
    []
  );

  const transformedEfficiencyData = Object.entries(
    weeklyEfficency?.response || {}
  )
    .map(([week, efficiency]) => ({
      name: week,
      efficiency,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalHours: 0,
      projects: 0,
      approved: 0,
      rejected: 0,
      pending: 0,
    },
    monthlyHours: [
      { month: "Jan", hours: 0 },
      { month: "Feb", hours: 0 },
      { month: "Mar", hours: 0 },
      { month: "Apr", hours: 0 },
      { month: "May", hours: 0 },
      { month: "Jun", hours: 0 },
    ],
    projects: [],
    timesheetStatus: [
      { name: "Approved", value: 0, color: statusColors.approved.main },
      { name: "Rejected", value: 0, color: statusColors.rejected.main },
      { name: "Pending", value: 0, color: statusColors.pending.main },
    ],
    recentActivity: [],
    productivityData: [
      { name: "Week 1", efficiency: 80 },
      { name: "Week 2", efficiency: 75 },
      { name: "Week 3", efficiency: 85 },
      { name: "Week 4", efficiency: 90 },
    ],
    completionRates: [
      { name: "Project A", value: 75, fill: statusColors.approved.main },
      { name: "Project B", value: 90, fill: statusColors.approved.main },
      { name: "Project C", value: 60, fill: statusColors.approved.main },
    ],
  });

  // Handle card closing
  const handleBirthdayCardClose = () => {
    setShowBirthdayCard(false);
    checkAllCardsClosed();
  };

  const handleWorkAnniversaryCardClose = () => {
    setShowWorkAnniversaryCard(false);
    checkAllCardsClosed();
  };

  const checkAllCardsClosed = () => {
    // Check if both cards are now closed
    if (!showBirthdayCard && !showWorkAnniversaryCard) {
      setShowCardsOverlay(false);
    }
  };

  // Modern styling
  const [greeting, setGreeting] = useState("");
  const [quote, setQuote] = useState("");

  // Motivational quotes
  const quotes = [
    "The only way to do great work is to love what you do.",
    "Your time is limited, don't waste it living someone else's life.",
    "Success is not final, failure is not fatal: It is the courage to continue that counts.",
    "Believe you can and you're halfway there.",
    "The future depends on what you do today.",
    "Don't watch the clock; do what it does. Keep going.",
  ];

  useEffect(() => {
    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");

    // Set random quote
    setQuote(quotes[Math.floor(Math.random() * quotes.length)]);

    // Fetch dashboard data
    dispatch(requestEmpDashboardList());
    dispatch(requestProjectsById());
    dispatch(requestEmpDashboardMonthlyHoursList());
    dispatch(requestTimesheetCount());
    dispatch(requestWeeklyEfficiency());
    dispatch(requestEmpWishes()); // Fetch employee wishes data for birthday/anniversary cards

    // Cleanup on unmount
    return () => {
      dispatch(setResetStateEmpDashboardList());
      dispatch(setResetStateProjectsById());
      dispatch(setResetStateEmpDashboardMonthlyHoursList());
      dispatch(setResetStateTimesheetCount());
    };
  }, [dispatch]);

  // Check if birthday card should be shown
  const isBirthdayMonth = () => {
    if (!userDetails?.dateOfBirth) return false;
    const birthDate = new Date(userDetails.dateOfBirth);
    if (isNaN(birthDate.getTime())) return false;
    const currentDate = new Date();
    return birthDate.getMonth() === currentDate.getMonth();
  };

  // Check if work anniversary card should be shown
  const isWorkAnniversary = () => {
    if (!userDetails?.joinDate) return false;
    const joinDate = new Date(userDetails.joinDate);
    if (isNaN(joinDate.getTime())) return false;
    const currentDate = new Date();
    const yearsOfService = currentDate.getFullYear() - joinDate.getFullYear();

    // Check if anniversary is in the current month and at least 1 year of service
    const isAnniversaryMonth = joinDate.getMonth() === currentDate.getMonth();
    const isAtLeastOneYear = yearsOfService >= 1;

    // Adjust for cases where the anniversary hasn't occurred this year yet
    if (yearsOfService === 1 && currentDate.getMonth() < joinDate.getMonth()) {
      return false;
    }

    return isAnniversaryMonth && isAtLeastOneYear;
  };

  // Process data from API response
  useEffect(() => {
    if (empDashboard && empDashboard.data) {
      const { data } = empDashboard;

      // Extract statistics from the API response using the exact structure
      const stats = {
        totalHours: data?.approvedHoursThisMonth || 0,
        projects: data?.totalProjects || 0,
        approved: data?.approvedTimesheetCount || 0,
        rejected: 0, // Not provided in the response
        pending: data.submittedTimesheetCount || 0,
      };

      // Update timesheet status data
      const timesheetStatus = [
        {
          name: "Approved",
          value: data.approvedTimesheetCount || 0,
          color: statusColors.approved.main,
        },
        {
          name: "Pending",
          value: data.submittedTimesheetCount || 0,
          color: statusColors.pending.main,
        },
        { name: "Rejected", value: 0, color: statusColors.rejected.main }, // Not provided in response
      ];

      // Update dashboard data state with the API response
      setDashboardData((prevState) => ({
        ...prevState,
        stats,
        timesheetStatus,
      }));
    }
  }, [empDashboard, statusColors]);

  const conditionalRowStyles = [
    {
      when: (row) => row.status === "APP",
      style: {
        backgroundColor: "#E0FFE0",
        color: "black",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#E0FFE0",
          color: "#black",
        },
      },
    },
    {
      when: (row) => row.status === "REJ",
      style: {
        backgroundColor: "#FF85A2",
        color: "black",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#FF85A2",
          color: "#black",
        },
      },
    },
  ];

  const columns = [
    {
      name: "Start Date",
      selector: (row) => {
        if (row && row.startDate) {
          try {
            return moment(row.startDate).format("MM-DD-YYYY");
          } catch (e) {
            return "Invalid date";
          }
        }
        return "";
      },
      sortable: true,
    },
    {
      name: "End Date",
      selector: (row) => {
        if (row && row.endDate) {
          try {
            return moment(row.endDate).format("MM-DD-YYYY");
          } catch (e) {
            return "Invalid date";
          }
        }
        return "";
      },
      sortable: true,
    },
    {
      name: "Location",
      selector: (row) => {
        if (row && row.work_location) {
          return typeof row.work_location === "string"
            ? row.work_location
            : String(row.work_location);
        }
        return "";
      },
      sortable: true,
      hide: "sm",
    },
    {
      name: "Total Hours",
      selector: (row) => {
        if (row && row.total_hours) {
          return typeof row.total_hours === "number"
            ? row.total_hours
            : String(row.total_hours);
        }
        return "";
      },
      sortable: true,
      hide: "sm",
    },
    {
      name: "Status",
      selector: (row) => {
        if (!row || !row.status) return "";

        const status =
          typeof row.status === "string" ? row.status : String(row.status);

        if (status === "SUB") {
          return (
            <>
              <PendingRounded size={24} data-tip data-for="submitted" />
              <CustomToolTip id="submitted" title="Submitted" />
            </>
          );
        } else if (status === "APP") {
          return (
            <>
              <CheckCircleRounded size={24} data-tip data-for="approved" />
              <CustomToolTip id="approved" title="Approved" />
            </>
          );
        } else if (status === "REJ") {
          return (
            <>
              <CancelRounded
                size={24}
                fill="red"
                data-tip
                data-for="rejected"
              />
              <CustomToolTip id="rejected" title="Rejected" />
            </>
          );
        } else {
          return status;
        }
      },
      sortable: true,
      hide: "sm",
    },
    {
      name: "Actions",
      cell: (row) => {
        if (!row) return null;

        const status = row.status
          ? typeof row.status === "string"
            ? row.status
            : String(row.status)
          : "";

        return (
          <div className="flex space-x-2">
            <div className="lg:hidden">
              {status === "APP" && (
                <span className="text-green-500 text-[12px]">Approved</span>
              )}
              {status === "REJ" && (
                <span className="text-red-500 text-[12px]">Rejected</span>
              )}
              {status === "SUB" && (
                <span className="text-yellow-500 text-[10px]">Submitted</span>
              )}
            </div>
            <button
              disabled={status === "APP"}
              onClick={() => handleEdit(row)}
              className={`text-primary hover:text-primaryDark disabled:cursor-not-allowed
              ${status === "APP" ? "hidden sm:block" : "block sm:hidden"}
              ${
                status === "REJ" || status === "SUB" || status === "RESUB"
                  ? "block lg:block"
                  : ""
              }
            `}
            >
              <CheckCircleRounded
                size={24}
                className={status === "APP" ? "text-gray-300" : "text-primary"}
              />
            </button>
          </div>
        );
      },
    },
  ];

  const handleClose = () => {
    dispatch(closeTimesheetModal());
    dispatch(setResetEditTimesheets());
    dispatch(setResetStateTimesheetById());
  };

  const handleEdit = (row) => {
    dispatch(setResetGetTimesheetById());
    setModalTitle("Edit TimeSheet");
    dispatch(requestGetTimesheetById(row.id));
  };

  const handleAddTimeSheet = (values, selectedFile) => {
    if (modalTitle === "Add TimeSheet") {
      // Create the holidays array properly
      const holidays = Object.values(values.holidays || {}).map(
        (holiday) => ({
          employee_holiday_date: holiday.date,
          notes: holiday.notes,
        })
      );

      const timeSheetPayload = {
        ...values,
        holidays: holidays,
      };

      dispatch(
        requestAddTimesheet({
          timeSheets: JSON.stringify([timeSheetPayload]),
          files: selectedFile || "null",
        })
      );
    } else {
      const editTimeSheetPayload = {
        work_location: values.work_location,
        startDate: values.startDate,
        endDate: values.endDate,
        total_hours: values.total_hours,
        holidays: values.holidays,
      };

      dispatch(
        requestEditTimesheets({
          id: timesheetByIdReducer?.Response?.id,
          timeSheet: JSON.stringify(editTimeSheetPayload),
          files: selectedFile || "null",
        })
      );
    }
  };

  // useEffect(() => {
  //   if (projectsList?.error && !projectsList?.loading) {
  //     dispatch(setResetStateProjectsById());
  //   }
  //   if (!selectedOption && projectsList?.response?.length > 0) {
  //     setSelectedOption(projectsList.response[0]); // Set first project as default
  //   }
  // }, [projectsList, dispatch, selectedOption]);

  useEffect(() => {
    if (!timesheetByIdReducer?.Loading && timesheetByIdReducer?.Response) {
      setModalTitle("Edit Timesheet");
    } else if (!timesheetByIdReducer?.Loading && timesheetByIdReducer?.Error) {
      toast.error("Error fetching Timesheet data");
      dispatch(setResetGetTimesheetById());
    }
  }, [timesheetByIdReducer]);

  useEffect(() => {
    if (empDashboardMonthlyHours?.response) {
      setChartData(
        Object.entries(empDashboardMonthlyHours.response).map(
          ([month, hours]) => ({
            month,
            hours,
          })
        )
      );
    } else {
      setChartData([]);
    }
    setResetStateEmpDashboardMonthlyHoursList();
  }, [empDashboardMonthlyHours]);

  useEffect(() => {
    if (AddTimeSheetSate.response && !AddTimeSheetSate.loading) {
      toast.success("Timesheet added successfully");
      dispatch(closeTimesheetModal());
      setModalTitle("");
      dispatch(setResetAddTimesheet());
      // Refresh dashboard data
      dispatch(requestEmpDashboardList());
      dispatch(requestTimesheetCount());
      dispatch(requestWeeklyEfficiency());
    } else if (AddTimeSheetSate.error && !AddTimeSheetSate.loading) {
      toast.error("Error adding Timesheet");
      dispatch(setResetAddTimesheet());
    }
  }, [AddTimeSheetSate, dispatch]);

  useEffect(() => {
    if (!editTimesheetReducer?.Loading && editTimesheetReducer?.Response) {
      toast.success("Timesheet updated successfully");
      handleClose();
      dispatch(setResetEditTimesheets());
      // Refresh dashboard data
      dispatch(requestEmpDashboardList());
      dispatch(requestTimesheetCount());
      dispatch(requestWeeklyEfficiency());
    } else if (!editTimesheetReducer?.Loading && editTimesheetReducer?.Error) {
      toast.error("Error updating Timesheet");
      dispatch(setResetEditTimesheets());
    }
  }, [editTimesheetReducer, dispatch]);

  // useEffect(() => {
  //   if (TimeSheets?.error && !TimeSheets?.loading ) {
  //     dispatch(setResetStateTimesheetsByIdForUserPage());
  //   }
  // }, [TimeSheets, dispatch]);

  // const handlePageChange = (page) => {
  //   dispatch(
  //     requestTimesheetsByIdForUserPage({
  //       project: selectedOption,
  //       page: page,
  //       size: TimeSheets?.size,
  //     })
  //   );
  // };

  const handleRowsChange = (size) => {
    dispatch(
      requestTimesheetsByIdForUserPage({
        project: selectedOption,
        page: 0,
        size: size,
      })
    );
  };

  // Check if wishes data is loaded and force show cards after login
  useEffect(() => {
    if (empWishes?.response) {

      // Check if we have either birthday or work anniversary wishes
      const hasBirthdayWishes = empWishes.response.birthday?.length;
      const hasWorkAnniversaryWishes =
        empWishes.response.workAnniversary?.length;

      // If we have any wishes, ensure cards are shown on fresh login
      if (hasBirthdayWishes || hasWorkAnniversaryWishes) {
        // Set which cards to show based on available data
        if (hasBirthdayWishes) {
          setShowBirthdayCard(true);
        }

        if (hasWorkAnniversaryWishes) {
          setShowWorkAnniversaryCard(true);
        }

        // Force overlay to show after login
        setShowCardsOverlay(true);
      }
    }
  }, [empWishes?.response]);

  // Determine if cards should be shown
  const shouldShowBirthdayCard =
    showBirthdayCard && empWishes?.response?.birthday?.length ;
  const shouldShowWorkAnniversaryCard =
    showWorkAnniversaryCard && empWishes?.response?.workAnniversary?.length ;
  const shouldShowCardOverlay =
    showCardsOverlay &&
    (shouldShowBirthdayCard || shouldShowWorkAnniversaryCard);

  // Force check on initial mount and when wishes data changes
  useEffect(() => {
    if (!shouldShowBirthdayCard && !shouldShowWorkAnniversaryCard) {
      setShowCardsOverlay(false);
    }
  }, [
    shouldShowBirthdayCard,
    shouldShowWorkAnniversaryCard,
    empWishes?.response,
  ]);

  // For debugging - this function can be called from browser console
  useEffect(() => {
    // Add this to window to allow manual triggering via browser console
    window.forceShowGreetingCards = () => {
      setShowBirthdayCard(true);
      setShowWorkAnniversaryCard(true);
      setShowCardsOverlay(true);

      return (
        "Cards overlay triggered. Check for API data: " +
        `Birthday wishes: ${empWishes?.response?.birthday?.length || 0}, ` +
        `Work Anniversary wishes: ${
          empWishes?.response?.workAnniversary?.length || 0
        }`
      );
    };

    // Cleanup
    return () => {
      delete window.forceShowGreetingCards;
    };
  }, [empWishes?.response]);

  // Summary cards data
  return (
    <div className="w-full h-[96vh] sm:h-[85vh] md:h-[92vh] lg:h-[96vh] overflow-y-auto sm:overflow-hidden px-0 py-3">
      <style>{customScrollbarStyles}</style>
      <AlertsCard />

      {/* Card Overlay */}
      <AnimatePresence>
        {shouldShowCardOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="card-overlay"
          >
            <div className="card-container p-4">
              {shouldShowBirthdayCard && (
                <div className="card-wrapper">
                  <BirthdayCard onClose={handleBirthdayCardClose} />
                </div>
              )}

              {shouldShowWorkAnniversaryCard && (
                <div className="card-wrapper">
                  <WorkAnniversaryCard
                    onClose={handleWorkAnniversaryCardClose}
                  />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dashboard Content */}
      <div className={`${shouldShowCardOverlay ? "dashboard-non-interactive" : ""} sm:h-auto md:h-[calc(96vh-4rem)] lg:h-[calc(96vh-4rem)] sm:min-h-[96vh] sm:overflow-y-auto md:overflow-y-auto lg:overflow-hidden px-5 py-3 custom-scrollbar`}>
        <Loader
          isLoading={
            empDashboard?.loading ||
            projectsList?.loading ||
            empDashboardMonthlyHours?.loading ||
            AddTimeSheetSate?.loading ||
            editTimesheetReducer?.loading
          }
        />

        {/* Welcome Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-xl shadow-lg mb-6 mx-4 mt-4">
          <div className="absolute inset-0 bg-pattern opacity-10"></div>

          <div className="relative z-10 p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
              <div>
                <h1 className="text-white text-2xl md:text-3xl font-bold mb-1">
                  {greeting}, {userDetails?.firstName || "User"}
                </h1>
                <p className="text-white/80 text-sm">
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              {/* <div className="mt-4 md:mt-0">
                <div className="px-4 py-1.5 bg-white/20 rounded-lg backdrop-blur-sm text-white text-sm font-medium">
                  Your productivity is up 12% this week
                </div>
              </div> */}
            </div>

            <div className="max-w-2xl mt-4">
              <p className="text-white/90 text-sm italic">"{quote}"</p>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="px-4 pb-8">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Hours Card */}
            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl shadow-sm p-4 border border-purple-200 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-purple-200 text-purple-700 mr-4">
                  <ClockIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-purple-700 font-medium">Hours This Month</div>
                  <div className="text-xl font-bold text-purple-900">
                    {empDashboard.response?.approvedHoursThisMonth}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="text-xs text-emerald-600 font-medium flex items-center">
                  <ChevronUpIcon className="h-3 w-3 mr-1" />
                  <span>Approved Hours</span>
                </div>
              </div>
            </div>

            {/* Projects Card */}
            <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl shadow-sm p-4 border border-blue-200 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-blue-200 text-blue-700 mr-4">
                  <BriefcaseIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-blue-700 font-medium">Active Projects</div>
                  <div className="text-xl font-bold text-blue-900">
                    {empDashboard.response?.totalProjects}
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-1">
                {CHART_COLORS_THEME.slice(0, 4).map((color, index) => (
                  <div
                    key={index}
                    className="h-1.5 rounded-full"
                    style={{ backgroundColor: color }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Approved Card */}
            <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-sm p-4 border border-emerald-200 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-emerald-200 text-emerald-700 mr-4">
                  <CheckCircleIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-emerald-700 font-medium">Approved Sheets</div>
                  <div className="text-xl font-bold text-emerald-900">
                    {empDashboard.response?.approvedTimesheetCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Pending Card */}
            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl shadow-sm p-4 border border-amber-200 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-amber-200 text-amber-700 mr-4">
                  <ClockIconSolid className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-amber-700 font-medium">Pending Approval</div>
                  <div className="text-xl font-bold text-amber-900">
                    {empDashboard.response?.submittedTimesheetCount}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="text-xs text-amber-600">
                  Submitted timesheets
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Monthly Performance Chart */}
            <div className="lg:col-span-2 bg-gradient-to-br from-white to-purple-50 rounded-xl shadow-sm p-5 border border-purple-100">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-purple-800 flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-purple-500 mr-2" />
                  Monthly Hours Trend
                </h3>
                <div className="px-2.5 py-1 text-xs rounded-full bg-purple-100 text-purple-600 font-medium">
                  Last 6 Months
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorHours"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={statusColors.trend.main}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={statusColors.trend.main}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: "#4B5563" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#4B5563" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => value}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderRadius: "0.5rem",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "none",
                      }}
                      formatter={(value) => [`${value} hrs`, "Hours"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="hours"
                      stroke={statusColors.trend.main}
                      fillOpacity={1}
                      fill="url(#colorHours)"
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Timesheet Status Chart */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-800 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-2" />
                  Timesheet Status
                </h3>
                <div className="px-2.5 py-1 text-xs rounded-full bg-purple-100 text-purple-600 font-medium">
                  Last 6 Months
                </div>
              </div>
              <div className="h-72 flex flex-col items-center justify-center">
                <div className="h-48 w-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Approved",
                            value: timesheetCount?.response?.approvedCount || 0,
                            color: statusColors.approved.light,
                          },
                          {
                            name: "Rejected",
                            value: timesheetCount?.response?.rejectedCount || 0,
                            color: statusColors.rejected.light,
                          },
                          {
                            name: "Pending",
                            value: timesheetCount?.response?.pendingCount || 0,
                            color: statusColors.pending.light,
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        onClick={(data) => {
                          navigate('/manage-timesheets');
                        }}
                      >
                        {[
                          {
                            name: "Approved",
                            value: timesheetCount?.response?.approvedCount || 0,
                            color: statusColors.approved.light,
                          },
                          {
                            name: "Rejected",
                            value: timesheetCount?.response?.rejectedCount || 0,
                            color: statusColors.rejected.light,
                          },
                          {
                            name: "Pending",
                            value: timesheetCount?.response?.pendingCount || 0,
                            color: statusColors.pending.light,
                          },
                        ].map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          borderRadius: "0.5rem",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          border: "none",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-4">
                  {[
                    {
                      name: "Approved",
                      color: statusColors.approved.light,
                      value: timesheetCount?.response?.approvedCount || 0,
                    },
                    {
                      name: "Rejected",
                      color: statusColors.rejected.light,
                      value: timesheetCount?.response?.rejectedCount || 0,
                    },
                    {
                      name: "Pending",
                      color: statusColors.pending.light,
                      value: timesheetCount?.response?.pendingCount || 0,
                    },
                  ].map((item, index) => (
                    <div 
                      key={index} 
                      className="flex items-center cursor-pointer"
                      onClick={() => navigate('/manage-timesheets')}
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm text-gray-700">
                        {item.name}: {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Projects Bar Chart */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-800 flex items-center">
                  <BriefcaseIcon className="h-5 w-5 text-blue-500 mr-2" />
                  Hours by Project
                </h3>
                <button className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center">
                  <span>View All</span>
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dashboardData.projects}
                    margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                    barSize={28}
                    layout="vertical"
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                      stroke="#f3f4f6"
                    />
                    <XAxis
                      type="number"
                      tick={{ fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                      width={150}
                      tickFormatter={(value) => {
                        return value?.length > 18
                          ? `${value.substring(0, 18)}...`
                          : value;
                      }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value} hrs`, "Hours"]}
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderRadius: "0.5rem",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "none",
                      }}
                    />
                    <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
                      {dashboardData.projects.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.fill ||
                            CHART_COLORS_THEME[index % CHART_COLORS_THEME.length]
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-800 flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-purple-500 mr-2" />
                  Weekly Efficiency
                </h3>
                <div className="px-2.5 py-1 text-xs rounded-full bg-purple-50 text-purple-600 font-medium">
                  This Month
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={transformedEfficiencyData}
                    margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                      domain={[0, 100]}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderRadius: "0.5rem",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "none",
                      }}
                      formatter={(value) => [`${value}%`, "Efficiency"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="efficiency"
                      stroke={statusColors.approved.main}
                      strokeWidth={3}
                      dot={{
                        r: 6,
                        strokeWidth: 0,
                        fill: statusColors.approved.main,
                      }}
                      activeDot={{
                        r: 8,
                        strokeWidth: 0,
                        fill: statusColors.approved.main,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div> */}

          {/* Additional Info Section */}
          {/* <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800 flex items-center">
                <UserGroupIcon className="h-5 w-5 text-indigo-500 mr-2" />
                Quick Links
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate("/manage-timesheets")}
                className="p-4 bg-indigo-50 rounded-xl flex flex-col items-center justify-center text-center hover:bg-indigo-100 transition-colors"
              >
                <ClockIcon className="h-8 w-8 text-indigo-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  Submit Timesheet
                </span>
              </button>

              <button
                onClick={() => navigate("/projects")}
                className="p-4 bg-blue-50 rounded-xl flex flex-col items-center justify-center text-center hover:bg-blue-100 transition-colors"
              >
                <BriefcaseIcon className="h-8 w-8 text-blue-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  View Projects
                </span>
              </button>

              <button
                onClick={() => navigate("/approvals")}
                className="p-4 bg-green-50 rounded-xl flex flex-col items-center justify-center text-center hover:bg-green-100 transition-colors"
              >
                <DocumentTextIcon className="h-8 w-8 text-green-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  Approvals
                </span>
              </button>

              <button
                onClick={() => navigate("/profile")}
                className="p-4 bg-purple-50 rounded-xl flex flex-col items-center justify-center text-center hover:bg-purple-100 transition-colors"
              >
                <UserGroupIcon className="h-8 w-8 text-purple-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">Profile</span>
              </button>
            </div>
          </div> */}
        </div>
      </div>

      {/* Timesheet Modal */}
      {modalTitle && (
        <TimeSheet
          title={modalTitle}
          onAddTimeSheet={(values, selectedFile) => {
            if (modalTitle === "Add TimeSheet") {
              // Create the holidays array properly
              const holidays = Object.values(values.holidays || {}).map(
                (holiday) => ({
                  employee_holiday_date: holiday.date,
                  notes: holiday.notes,
                })
              );

              const timeSheetPayload = {
                ...values,
                holidays: holidays,
              };

              dispatch(
                requestAddTimesheet({
                  timeSheets: JSON.stringify([timeSheetPayload]),
                  files: selectedFile || "null",
                })
              );
            } else {
              const editTimeSheetPayload = {
                work_location: values.work_location,
                startDate: values.startDate,
                endDate: values.endDate,
                total_hours: values.total_hours,
                holidays: values.holidays,
              };

              dispatch(
                requestEditTimesheets({
                  id: timesheetByIdReducer?.Response?.id,
                  timeSheet: JSON.stringify(editTimeSheetPayload),
                  files: selectedFile || "null",
                })
              );
            }
          }}
          timesheetValues={timesheetByIdReducer?.Response}
          projectsList={projectsList?.response || []}
          isLoading={AddTimeSheetSate.loading || editTimesheetReducer?.Loading}
        />
      )}
    </div>
  );
};

export default Hoc(EmployeeDashboard);
